import {
  HELP_DOCUMENT_VERSION,
  HELP_SECTION_PRINCIPAL_ID,
  HelpDocument,
  HelpSection,
} from '../models/help-section.model';

const DOCUMENT_MARKER = `data-help-document="${HELP_DOCUMENT_VERSION}"`;

/**
 * Crea la sección principal vacía
 * @param title - Título de la sección
 * @returns - Sección principal
 */
export function createPrincipalSection(titleKey = HELP_SECTION_PRINCIPAL_ID): HelpSection {
  return {
    id: HELP_SECTION_PRINCIPAL_ID,
    title: titleKey,
    content: `<h2>${escapeHtml(titleKey)}</h2><p></p>`,
    order: 0,
  };
}

/**
 * Normaliza entrada del usuario a clave i18n (slug: modo-mapa).
 */
export function normalizeSectionTitleKey(input: string): string {
  return slugifyTitle(input.trim());
}

/**
 * Lista vacía: el usuario crea secciones manualmente en modo menú
 * @returns - Lista de secciones
 */
export function createDefaultSections(): HelpSection[] {
  return [];
}

/**
 * Reasigna order 0..n-1 según el array ordenado
 * @param sections - Secciones
 * @returns - Secciones con order asignado
 */
export function normalizeSectionOrders(sections: HelpSection[]): HelpSection[] {
  return sections.map((s, index) => ({ ...s, order: index }));
}

/**
 * Intercambia posición de una sección (-1 subir, +1 bajar)
 * @param sections - Secciones
 * @param sectionId - ID de la sección
 * @param offset - Offset (-1 subir, +1 bajar)
 * @returns - Secciones con posición intercambiada
 */
export function moveSectionByOffset(
  sections: HelpSection[],
  sectionId: string,
  offset: -1 | 1,
): HelpSection[] {
  const sorted = [...sections].sort((a, b) => a.order - b.order);
  const index = sorted.findIndex((s) => s.id === sectionId);
  if (index < 0) return sections;

  const target = index + offset;
  if (target < 0 || target >= sorted.length) return sections;

  [sorted[index], sorted[target]] = [sorted[target], sorted[index]];
  return normalizeSectionOrders(sorted);
}

/**
 * Slug estable para data-id
 * @param title - Título de la sección
 * @returns - Slug estable
 */
export function slugifyTitle(title: string): string {
  const base = String(title)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return base || 'seccion';
}

/**
 * Asegura id único frente a secciones existentes
 * @param title - Título de la sección
 * @param existing - Secciones existentes
 * @returns - ID único
 */
export function uniqueSectionId(title: string, existing: HelpSection[]): string {
  let id = slugifyTitle(title);
  let n = 1;
  while (existing.some((s) => s.id === id)) {
    id = `${slugifyTitle(title)}-${n++}`;
  }
  return id;
}

/**
 * Interpreta HTML almacenado en BD.
 * Sin marcador v1 → legacy (sin menú lateral).
 * @param html - HTML a interpretar
 * @returns - Documento interpretado
 */
export function parseHelpDocument(html: string | null | undefined): HelpDocument {
  const raw = (html ?? '').trim();
  if (!raw || !raw.includes(DOCUMENT_MARKER)) {
    return { mode: 'legacy', sections: [], legacyHtml: raw };
  }

  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    const root = doc.querySelector(`div[data-help-document="${HELP_DOCUMENT_VERSION}"]`);

    if (!root) {
      return { mode: 'legacy', sections: [], legacyHtml: raw };
    }

    const sectionEls = root.querySelectorAll('section[data-id]');
    const sections: HelpSection[] = [];

    sectionEls.forEach((el, index) => {
      const id = el.getAttribute('data-id') ?? `seccion-${index}`;
      const dataTitle = el.getAttribute('data-title') ?? id;
      // Menú lateral: clave i18n en data-title (data-id permanece estable al renombrar)
      const title = normalizeSectionTitleKey(dataTitle) || id;
      const orderAttr = el.getAttribute('data-order');
      const order = orderAttr != null && orderAttr !== '' ? Number(orderAttr) : index;
      sections.push({
        id,
        title,
        content: el.innerHTML.trim(),
        order: Number.isFinite(order) ? order : index,
      });
    });

    if (sections.length === 0) {
      return { mode: 'legacy', sections: [], legacyHtml: raw };
    }

    sections.sort((a, b) => a.order - b.order);
    return { mode: 'sections', sections };
  } catch {
    return { mode: 'legacy', sections: [], legacyHtml: raw };
  }
}

/**
 * Serializa secciones al formato HTML v1
 * @param sections - Secciones
 * @returns - HTML serializado
 */
export function serializeHelpDocument(sections: HelpSection[]): string {
  if (!sections.length) {
    return '';
  }
  const sorted = [...sections].sort((a, b) => a.order - b.order);
  const body = sorted
    .map(
      (s) =>
        `<section data-id="${escapeAttr(s.id)}" data-title="${escapeAttr(s.title)}" data-order="${s.order}">${s.content}</section>`,
    )
    .join('\n');

  return `<div data-help-document="${HELP_DOCUMENT_VERSION}">\n${body}\n</div>`;
}

/**
 * HTML plano para PDF o títulos (concatena secciones con h2)
 * @param html - HTML a flatten
 * @returns - HTML flatten
 */
export function flattenHelpDocument(html: string | null | undefined): string {
  const doc = parseHelpDocument(html);
  if (doc.mode === 'legacy') {
    return doc.legacyHtml ?? '';
  }
  return doc.sections
    .map((s) => {
      const hasH2 = /<h2[\s>]/i.test(s.content);
      const heading = hasH2 ? '' : `<h2>${escapeHtml(s.title)}</h2>`;
      return `${heading}${s.content}`;
    })
    .join('\n');
}

/**
 * Título para listados: principal o primer h2 del documento
 * @param html - HTML a extraer el título
 * @param fallback - Fallback si no se encuentra el título
 * @returns - Título extraído
 */
export function extractDocumentTitle(html: string | null | undefined, fallback = ''): string {
  const doc = parseHelpDocument(html);
  if (doc.mode === 'sections' && doc.sections.length > 0) {
    const first = doc.sections[0];
    if (first?.title) {
      return first.title;
    }
  }
  const flat = doc.mode === 'legacy' ? doc.legacyHtml : flattenHelpDocument(html);
  const match = flat?.match(/<h2[^>]*>(.*?)<\/h2>/is);
  if (match) {
    return match[1].replace(/<[^>]*>/g, '').trim();
  }
  return fallback;
}

/**
 * Texto sin etiquetas para validar contenido
 * @param html - HTML a convertir a texto plano
 * @returns - Texto plano
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

/**
 * Clona secciones (p. ej. al cambiar de idioma conservando estructura)
 * @param from - Secciones a clonar
 * @param contentById - Contenido por ID
 * @returns - Secciones clonadas
 */
export function cloneSectionsStructure(
  from: HelpSection[],
  contentById: Record<string, string>,
): HelpSection[] {
  return from.map((s) => ({
    ...s,
    content: contentById[s.id] ?? '',
  }));
}

/**
 * Alinea estructura de secciones entre idiomas (mismos id/title/order)
 * @param structure - Estructura de secciones
 * @param contentSections - Secciones de contenido
 * @returns - Secciones alineadas
 */
export function alignSectionsToStructure(
  structure: HelpSection[],
  contentSections: HelpSection[],
): HelpSection[] {
  const contentMap = new Map(contentSections.map((s) => [s.id, s.content]));
  return structure.map((s) => ({
    id: s.id,
    title: s.title,
    order: s.order,
    content: contentMap.get(s.id) ?? '',
  }));
}

/**
 * Escapa HTML
 * @param text - Texto a escapar
 * @returns - Texto escapado
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Escapa atributos HTML
 * @param text - Texto a escapar
 * @returns - Texto escapado
 */
function escapeAttr(text: string): string {
  return escapeHtml(text).replace(/'/g, '&#39;');
}
